import React, { useState, useEffect, useMemo } from "react";
import { Spinner, Alert } from "react-bootstrap";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell } from "recharts";
import { getAnalyticsWidget } from "../../../utils/Requests";
import Logger from "../../../utils/Logger";
import { AnalyticWidgetProps } from "./types";

type SortDirection = "asc" | "desc";
type SlotSortField = "experiment_title" | "invocation_count" | "play_count" | "conversion_rate";

// Type workarounds for recharts components
const RESP: any = ResponsiveContainer as any;
const BC: any = BarChart as any;
const BR: any = Bar as any;
const XA: any = XAxis as any;
const YA: any = YAxis as any;
const CG: any = CartesianGrid as any;
const TT: any = Tooltip as any;
const LG: any = Legend as any;
const CL: any = Cell as any;

/* eslint-disable camelcase */
type SlotDataPoint = {
    slot_id: string;
    experiment_title: string;
    play_count: number;
    invocation_count: number;
    conversion_rate: number;
};
/* eslint-enable camelcase */

// Color palette for bars
const COLORS = [
    "#8884d8", "#82ca9d", "#ffc658", "#ff7c7c", "#8b5cf6",
    "#06b6d4", "#10b981", "#f59e0b", "#ef4444", "#6366f1"
];

/**
 * Slot Performance Widget - Play counts and conversion rates by recommendation slot
 */
const SlotPerformanceWidget: React.FC<AnalyticWidgetProps> = ({
    customerConfig,
    filters,
    theme,
    chartOptions: _chartOptions
}) => {
    const [analyticsData, setAnalyticsData] = useState<SlotDataPoint[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sortField, setSortField] = useState<SlotSortField>("play_count");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

    const sortedData = useMemo(() => {
        return [...analyticsData].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];
            if (typeof aVal === "string" && typeof bVal === "string") {
                return sortDirection === "asc" 
                    ? aVal.localeCompare(bVal) 
                    : bVal.localeCompare(aVal);
            }
            return sortDirection === "asc" 
                ? (aVal as number) - (bVal as number) 
                : (bVal as number) - (aVal as number);
        });
    }, [analyticsData, sortField, sortDirection]);

    const handleSort = (field: SlotSortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("desc");
        }
    };

    const SortIcon = ({ field }: { field: SlotSortField }) => (
        <span style={{ marginLeft: "5px", opacity: sortField === field ? 1 : 0.3 }}>
            {sortField === field && sortDirection === "asc" ? "▲" : "▼"}
        </span>
    );

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            setIsLoading(true);
            setError(null);
            
            try {
                const response = await getAnalyticsWidget(
                    customerConfig,
                    "slot_performance",
                    filters.startDate,
                    filters.endDate,
                    filters.attributionId,
                    filters.brandId,
                    filters.deviceType,
                    filters.focusField
                );
                
                const data = (response as any)?.result;
                const dataArray = Array.isArray(data) ? data : [];
                setAnalyticsData(dataArray);
            } catch (err) {
                Logger.error("Error fetching slot performance data", err);
                setError("Failed to fetch slot performance data");
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [customerConfig, filters]);

    if (isLoading) {
        return (
            <div className="text-center" style={{ padding: "40px" }}>
                <Spinner animation="border" role="status">
                    <span className="sr-only">Loading...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ marginBottom: "40px" }}>
                <h4>Slot Performance</h4>
                <Alert variant="danger">{error}</Alert>
            </div>
        );
    }

    // Calculate chart height based on number of data points (min 300, max 600)
    const chartHeight = Math.min(600, Math.max(300, analyticsData.length * 40));

    return (
        <div style={{ marginBottom: "40px" }}>
            <h4>Slot Performance</h4>
            
            {analyticsData.length === 0 ? (
                <Alert variant="info">No data available for the selected date range.</Alert>
            ) : (
                <>
                    <div style={{ 
                        backgroundColor: theme.chartBg, 
                        padding: "20px", 
                        borderRadius: "5px",
                        marginBottom: "20px"
                    }}>
                        <RESP width="100%" height={chartHeight}>
                            <BC 
                                data={analyticsData} 
                                layout="vertical"
                                margin={{ top: 5, right: 30, left: 150, bottom: 5 }}
                            >
                                <CG strokeDasharray="3 3" />
                                <XA type="number" />
                                <YA 
                                    type="category" 
                                    dataKey="experiment_title" 
                                    width={140}
                                    tick={{ fontSize: 12 }}
                                />
                                <TT 
                                    formatter={(value: number, name: string) => {
                                        if (name === "Conversion Rate") return `${value}%`;
                                        return value.toLocaleString();
                                    }}
                                    labelFormatter={(label: string) => `Slot: ${label}`}
                                />
                                <LG />
                                <BR 
                                    dataKey="play_count" 
                                    name="Play Count"
                                    fill="#82ca9d"
                                >
                                    {analyticsData.map((_entry, index) => (
                                        <CL key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </BR>
                            </BC>
                        </RESP>
                    </div>

                    {/* Summary table */}
                    <div style={{ 
                        backgroundColor: theme.chartBg, 
                        padding: "15px", 
                        borderRadius: "5px",
                        marginBottom: "20px",
                        overflowX: "auto"
                    }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ borderBottom: "2px solid #ddd" }}>
                                    <th 
                                        style={{ padding: "10px", textAlign: "left", cursor: "pointer" }}
                                        onClick={() => handleSort("experiment_title")}
                                    >
                                        Experiment<SortIcon field="experiment_title" />
                                    </th>
                                    <th 
                                        style={{ padding: "10px", textAlign: "right", cursor: "pointer" }}
                                        onClick={() => handleSort("invocation_count")}
                                    >
                                        Invocations<SortIcon field="invocation_count" />
                                    </th>
                                    <th 
                                        style={{ padding: "10px", textAlign: "right", cursor: "pointer" }}
                                        onClick={() => handleSort("play_count")}
                                    >
                                        Plays<SortIcon field="play_count" />
                                    </th>
                                    <th 
                                        style={{ padding: "10px", textAlign: "right", cursor: "pointer" }}
                                        onClick={() => handleSort("conversion_rate")}
                                    >
                                        Conversion Rate<SortIcon field="conversion_rate" />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedData.map((row, idx) => (
                                    <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                                        <td style={{ padding: "10px" }}>{row.experiment_title}</td>
                                        <td style={{ padding: "10px", textAlign: "right" }}>
                                            {row.invocation_count.toLocaleString()}
                                        </td>
                                        <td style={{ padding: "10px", textAlign: "right" }}>
                                            {row.play_count.toLocaleString()}
                                        </td>
                                        <td style={{ padding: "10px", textAlign: "right" }}>
                                            {row.conversion_rate}%
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <details>
                        <summary style={{ cursor: "pointer", fontWeight: "bold", marginBottom: "10px" }}>
                            View Raw Data
                        </summary>
                        <pre style={{ 
                            backgroundColor: "#f5f5f5", 
                            padding: "15px", 
                            borderRadius: "5px",
                            maxHeight: "400px",
                            overflow: "auto"
                        }}>
                            {JSON.stringify(analyticsData, null, 2)}
                        </pre>
                    </details>
                </>
            )}
        </div>
    );
};

export default SlotPerformanceWidget;
